// src/store/thunks/userThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import userService from "../../services/userService";

// ========== USER MANAGEMENT THUNKS ==========

export const getUsersForAdminThunk = createAsyncThunk(
  "user/getUsersForAdmin",
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await userService.getUsersForAdmin(queryParams);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const approveRejectMultipleUsersThunk = createAsyncThunk(
  "user/approveRejectMultipleUsers",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.approveRejectMultipleUsers(userData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const blockUnblockMultipleUsersThunk = createAsyncThunk(
  "user/blockUnblockMultipleUsers",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.blockUnblockMultipleUsers(userData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteMultipleUsersThunk = createAsyncThunk(
  "user/deleteMultipleUsers",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.deleteMultipleUsers(userData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const createUserByAdminThunk = createAsyncThunk(
  "user/createUserByAdmin",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.createUserByAdmin(userData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserByAdminThunk = createAsyncThunk(
  "user/updateUserByAdmin",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUserByAdmin(userId, userData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== USER REQUEST THUNKS ==========

export const getUserRequestsThunk = createAsyncThunk(
  "user/getUserRequests",
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await userService.getUserRequests(queryParams);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const handleUserRequestThunk = createAsyncThunk(
  "user/handleUserRequest",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await userService.handleUserRequest(requestData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteUserRequestThunk = createAsyncThunk(
  "user/deleteUserRequest",
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await userService.deleteUserRequest(requestId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteMultipleUserRequestsThunk = createAsyncThunk(
  "user/deleteMultipleUserRequests",
  async (requestIds, { rejectWithValue }) => {
    try {
      const response = await userService.deleteMultipleUserRequests(requestIds);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== APPOINTMENT ANALYTICS THUNKS ==========

export const getUserAppointmentStatisticsThunk = createAsyncThunk(
  "user/getUserAppointmentStatistics",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserAppointmentStatistics(userId);
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUserAppointmentInsightsThunk = createAsyncThunk(
  "user/getUserAppointmentInsights",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserAppointmentInsights(userId);
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUserBehaviorAnalysisThunk = createAsyncThunk(
  "user/getUserBehaviorAnalysis",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserBehaviorAnalysis(userId);
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAggregateStatisticsThunk = createAsyncThunk(
  "user/getAggregateStatistics",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getAggregateStatistics(filters);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getTopCustomersByAppointmentsThunk = createAsyncThunk(
  "user/getTopCustomersByAppointments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getTopCustomersByAppointments(params);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getHighRiskCustomersThunk = createAsyncThunk(
  "user/getHighRiskCustomers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getHighRiskCustomers(params);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getVIPCustomersThunk = createAsyncThunk(
  "user/getVIPCustomers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getVIPCustomers(params);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchPerformanceThunk = createAsyncThunk(
  "user/getBranchPerformance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getBranchPerformance();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== USER ANALYTICS DASHBOARD THUNK ==========

export const getUserAnalyticsThunk = createAsyncThunk(
  "user/getUserAnalytics",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserAnalytics(userId);
      return { userId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== BRANCH MANAGEMENT THUNKS ==========

export const getUserBranchPreferencesThunk = createAsyncThunk(
  "user/getUserBranchPreferences",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserBranchPreferences(userId);
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserPrimaryBranchThunk = createAsyncThunk(
  "user/updateUserPrimaryBranch",
  async ({ userId, branchData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUserPrimaryBranch(
        userId,
        branchData
      );
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== VISIT & PURCHASE HISTORY THUNKS ==========

export const getUserVisitHistoryThunk = createAsyncThunk(
  "user/getUserVisitHistory",
  async ({ userId, branchCode }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserVisitHistory(
        userId,
        branchCode
      );
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUserPurchaseHistoryThunk = createAsyncThunk(
  "user/getUserPurchaseHistory",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserPurchaseHistory(userId);
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUserCustomerProfileThunk = createAsyncThunk(
  "user/getUserCustomerProfile",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserCustomerProfile(userId);

      console.log("Response in Thunk:", response);
      return { userId, profile: response.profile };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserCustomerProfileThunk = createAsyncThunk(
  "user/updateUserCustomerProfile",
  async ({ userId, profileData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUserCustomerProfile(
        userId,
        profileData
      );
      return { userId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== LOYALTY THUNKS ==========

export const getUserLoyaltyInfoThunk = createAsyncThunk(
  "user/getUserLoyaltyInfo",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserLoyaltyInfo(userId);
      return { userId, data: response };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserLoyaltyPointsThunk = createAsyncThunk(
  "user/updateUserLoyaltyPoints",
  async ({ userId, pointsData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUserLoyaltyPoints(
        userId,
        pointsData
      );
      return { userId, loyalty: response };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== BULK OPERATIONS THUNKS ==========

export const getBulkUsersThunk = createAsyncThunk(
  "user/getBulkUsers",
  async (userIds, { rejectWithValue }) => {
    try {
      const response = await userService.getBulkUsers(userIds);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyUserKYCThunk = createAsyncThunk(
  "user/verifyUserKYC",
  async ({ userId, verified }, { rejectWithValue }) => {
    try {
      const response = await userService.verifyUserKYC(userId, verified);
      return { userId, verified, response };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== ACTIONS ==========

export const clearUserError = createAction("user/clearUserError");
export const clearUserAnalytics = createAction("user/clearUserAnalytics");
export const selectUser = createAction("user/selectUser");
export const clearSelectedUser = createAction("user/clearSelectedUser");
